import { authSessionStorage } from "~/utils/helpers/auth/browser-context";
import type { ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";
import { CopilotDataPartEnum, CopilotPartEnum, CopilotRoleEnum } from "~/utils/enums/copilot/chat.enums";
import { copilotResourceCards } from "~/utils/helpers/copilot/resources";
import { normalizeCopilotError } from "~/utils/helpers/copilot/errors";
import { CopilotErrorRecoveryEnum } from "~/utils/enums/copilot/errors.enums";

type HistoryStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const HISTORY_KEY = "simbel.copilot.conversation.v1";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Persist readable history only: no PDF bytes, signed document URLs, approvals, or tool outputs. */
function historyMessagesOf(value: unknown): ICopilotUIMessage[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((message) => {
    if (!isRecord(message) || typeof message.id !== "string" || !Array.isArray(message.parts)) return [];
    if (message.role !== CopilotRoleEnum.USER && message.role !== CopilotRoleEnum.ASSISTANT) return [];
    const parts: ICopilotUIMessage["parts"] = [];

    for (const part of message.parts) {
      if (!isRecord(part)) continue;
      if (part.type === CopilotPartEnum.TEXT && typeof part.text === "string") {
        parts.push({ type: CopilotPartEnum.TEXT, text: part.text });
      } else if (
        part.type === `data-${CopilotDataPartEnum.RESOURCES}` &&
        isRecord(part.data) &&
        Array.isArray(part.data.items)
      ) {
        const items = part.data.items.flatMap((item) => {
          const [card] = copilotResourceCards({ items: [item] });
          if (!card || !isRecord(item) || typeof item.id !== "string") return [];
          const { href: _href, ...fields } = card;
          return [{ ...fields, id: item.id }];
        });
        if (items.length) parts.push({ type: `data-${CopilotDataPartEnum.RESOURCES}`, data: { items } });
      } else if (part.type === `data-${CopilotDataPartEnum.ERROR}`) {
        // Attachments are intentionally absent, so replaying their failed request would be incomplete.
        parts.push({
          type: `data-${CopilotDataPartEnum.ERROR}`,
          data: { ...normalizeCopilotError(part), recovery: CopilotErrorRecoveryEnum.NONE },
        });
      }
    }

    return parts.length ? [{ id: message.id, role: message.role, parts }] : [];
  });
}

/** One snapshot per tab, discarded when the authenticated context changes. Storage is optional. */
export function createCopilotHistory(storage?: HistoryStorage) {
  const storageOf = () => storage ?? (typeof window === "undefined" ? undefined : authSessionStorage());
  function clear() {
    try {
      storageOf()?.removeItem(HISTORY_KEY);
    } catch {
      /* Storage may be disabled in embedded browsers. */
    }
  }

  return {
    load(scope: string): { chatId: string; messages: ICopilotUIMessage[] } | null {
      try {
        const snapshot: unknown = JSON.parse(storageOf()?.getItem(HISTORY_KEY) ?? "null");
        if (
          isRecord(snapshot) &&
          snapshot.scope === scope &&
          typeof snapshot.chatId === "string" &&
          Array.isArray(snapshot.messages)
        ) {
          return { chatId: snapshot.chatId, messages: historyMessagesOf(snapshot.messages) };
        }
      } catch {
        /* Corrupt or unavailable history starts a fresh conversation. */
      }
      clear();
      return null;
    },
    save(scope: string, chatId: string, messages: ICopilotUIMessage[]) {
      try {
        storageOf()?.setItem(HISTORY_KEY, JSON.stringify({ scope, chatId, messages: historyMessagesOf(messages) }));
      } catch {
        clear();
      }
    },
    clear,
  };
}
